import React, { useEffect, useRef, useState } from 'react';
import { Activity, Cpu, TestTube, ShoppingCart, BarChart2 } from 'lucide-react';
import { getKpiStatus } from '../kpiUtils';

const useCountUp = (target, duration = 900) => {
  const [value, setValue] = useState(0);
  const frame = useRef(null);
  const safeTarget = Math.max(0, target || 0);

  useEffect(() => {
    setValue(0);
    if (safeTarget === 0) return;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * safeTarget));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [safeTarget, duration]);

  return value;
};

const KpiTile = ({ icon: Icon, label, rawValue, displayValue, status, onClick, suffix = '' }) => {
  const animated = useCountUp(typeof rawValue === 'number' ? rawValue : 0);
  const show = typeof rawValue === 'number' ? `${animated}${suffix}` : displayValue;

  const bgMap = {
    healthy: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    warning: 'bg-amber-50 border-amber-200 text-amberald-700',
    critical: 'bg-red-50 border-red-200 text-red-700',
    neutral: 'bg-slate-50 border-slate-200 text-slate-600',
  };

  const numColor = {
    healthy: 'text-emerald-700',
    warning: 'text-amber-700',
    critical: 'text-red-700',
    neutral: 'text-slate-800',
  };

  const tileClass = bgMap[status] || bgMap.neutral;
  const numClass = numColor[status] || numColor.neutral;

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1 rounded-xl border p-3 text-center transition-all hover:shadow-sm cursor-pointer flex-1 min-w-0 ${tileClass}`}
    >
      <Icon className={`w-4 h-4 shrink-0 ${numClass}`} />
      <p className={`text-2xl font-bold tabular-nums leading-none ${numClass}`}>{show}</p>
      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500 leading-tight w-full truncate px-1">{label}</p>
    </button>
  );
};

export default function ExecutiveHero({ data }) {
  if (!data) return null;

  const { stats, teamProgress, healthScore, atRiskCount } = data;

  const scroll = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const pct = Math.max(0, teamProgress.percentage || 0);
  const healthStatus = getKpiStatus(healthScore, 80, 50);
  const fullSetCapacity = data.hwFullSet?.max_capacity ?? data.deviceCap?.max_capacity ?? stats.hardware_sets_capacity ?? 0;
  const deviceCapStatus = getKpiStatus(fullSetCapacity, 20, 10);
  const testStatus = getKpiStatus(stats.testCapacity, 100, 50);
  const riskStatus = getKpiStatus(atRiskCount, 0, 2, true);
  const productionStatus = getKpiStatus(pct, 90, 60);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
      <KpiTile icon={Activity} label="Health Score" rawValue={healthScore} status={healthStatus} onClick={() => scroll('insights')} />
      <KpiTile icon={Cpu} label="Full Set Capacity" rawValue={fullSetCapacity} status={deviceCapStatus} onClick={() => scroll('capacity')} />
      <KpiTile icon={TestTube} label="Test Capacity" rawValue={stats.testCapacity} status={testStatus} onClick={() => scroll('capacity')} />
      <KpiTile icon={ShoppingCart} label="Orders At Risk" rawValue={atRiskCount} status={riskStatus} onClick={() => scroll('orders')} />
      <KpiTile icon={BarChart2} label="Production Today" rawValue={pct} suffix="%" status={productionStatus} onClick={() => scroll('workforce')} />
    </div>
  );
}